import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    X,
    Check,
    ChevronDown,
    Leaf,
    Target,
    Heart,
    Briefcase,
    SlidersHorizontal,
    Palette,
    Lock,
    Download,
    HelpCircle,
    Sun,
    Sparkles,
    Activity,
    SplitSquareHorizontal,
    ShieldCheck,
    Volume2,
    VolumeX,
    BarChart3,
    LifeBuoy,
    Pencil,
    Camera,
    Trash2,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Communication style — 4 elegant rows with colored icon tiles.
// `internal` maps to the engine's style key (concise|warm|firm|professional|gentle)
// so the rewrite cascade in AnalysisCard can resolve a returned version.
// `null` (no card selected) = no soft preference, let intent decide.
// ─────────────────────────────────────────────────────────────────────────────
const STYLE_OPTIONS = [
    {
        key: "calm",
        label: "Calm & Thoughtful",
        internal: "gentle",
        icon: Leaf,
        iconClass: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100",
    },
    {
        key: "direct",
        label: "Direct & Concise",
        internal: "concise",
        icon: Target,
        iconClass: "bg-violet-50 text-violet-700 ring-1 ring-violet-100",
    },
    {
        key: "warm",
        label: "Warm & Friendly",
        internal: "warm",
        icon: Heart,
        iconClass: "bg-rose-50 text-rose-600 ring-1 ring-rose-100",
    },
    {
        key: "professional",
        label: "Professional & Neutral",
        internal: "professional",
        icon: Briefcase,
        iconClass: "bg-sky-50 text-sky-700 ring-1 ring-sky-100",
    },
];

const TONEMIRROR_OPTIONS = [
    {
        key: "emotionalNudges",
        icon: Sparkles,
        label: "Emotional Nudges",
        hint: "Gentle notes like \u201cThis rewrite sounds less confrontational.\u201d",
    },
    {
        key: "patternTracking",
        icon: Activity,
        label: "Pattern Tracking",
        hint: "Notice recurring shifts \u2014 urgency, escalation, apologies.",
    },
    {
        key: "rewriteComparison",
        icon: SplitSquareHorizontal,
        label: "Rewrite Comparison",
        hint: "See the original and the rewrite side by side, in context.",
    },
    {
        key: "privacyMode",
        icon: ShieldCheck,
        label: "Privacy Mode",
        hint: "Stop sending anonymous analytics events. Chats still sync to your account.",
    },
];

const APP_VERSION = "v1.0.0";

// ─────────────────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────────────────
const ProfilePanel = ({
    open,
    onClose,
    userProfile = { name: "Friend" },
    onChangeUserProfile,
    defaultStyle = null,
    onChangeDefaultStyle,
    tonemirrorPrefs = {},
    onChangeTonemirrorPref,
    soundEnabled = false,
    onToggleSound,
    onResetAll,
    onLogout,
    accountEmail = null,
    onExport,
    chatsCount = 0,
}) => {
    const panelRef = useRef(null);
    const mobileRef = useRef(null);

    const [isDesktop, setIsDesktop] = useState(() =>
        typeof window === "undefined"
            ? true
            : window.matchMedia("(min-width: 768px)").matches,
    );
    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const mql = window.matchMedia("(min-width: 768px)");
        const onChange = (e) => setIsDesktop(e.matches);
        mql.addEventListener?.("change", onChange);
        return () => mql.removeEventListener?.("change", onChange);
    }, []);

    // ESC to close.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    // Click outside to close. The avatar trigger marks itself with
    // [data-tg-profile-trigger] so its click is treated as the toggle,
    // not an "outside" close. CRITICAL: defer attachment by one frame after
    // `open` flips to true so the opening tap can't self-cancel.
    useEffect(() => {
        if (!open) return undefined;
        let attached = false;
        const onDocPointerDown = (e) => {
            if (panelRef.current && panelRef.current.contains(e.target)) return;
            if (mobileRef.current && mobileRef.current.contains(e.target)) return;
            if (
                e.target.closest &&
                e.target.closest("[data-tg-profile-trigger]")
            ) {
                return;
            }
            onClose();
        };
        const raf = window.requestAnimationFrame(() => {
            document.addEventListener("mousedown", onDocPointerDown);
            document.addEventListener("touchstart", onDocPointerDown, {
                passive: true,
            });
            attached = true;
        });
        return () => {
            window.cancelAnimationFrame(raf);
            if (attached) {
                document.removeEventListener("mousedown", onDocPointerDown);
                document.removeEventListener("touchstart", onDocPointerDown);
            }
        };
    }, [open, onClose]);

    return (
        <div
            data-testid="profile-panel-root"
            data-open={open}
            aria-hidden={!open}
        >
            {isDesktop ? (
                <div
                    ref={panelRef}
                    data-testid="profile-panel"
                    role="dialog"
                    aria-label="Profile and preferences"
                    className={[
                        "fixed top-0 bottom-0 left-0 w-full md:w-[320px] flex flex-col z-[60]",
                        "bg-card border-r border-border",
                        "shadow-[8px_0_28px_-18px_rgba(40,40,40,0.18)]",
                        "transition-transform duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)]",
                        open
                            ? "translate-x-0"
                            : "-translate-x-full pointer-events-none",
                    ].join(" ")}
                >
                    <PanelContent
                        onClose={onClose}
                        userProfile={userProfile}
                        onChangeUserProfile={onChangeUserProfile}
                        defaultStyle={defaultStyle}
                        onChangeDefaultStyle={onChangeDefaultStyle}
                        tonemirrorPrefs={tonemirrorPrefs}
                        onChangeTonemirrorPref={onChangeTonemirrorPref}
                        soundEnabled={soundEnabled}
                        onToggleSound={onToggleSound}
                        onResetAll={onResetAll}
                        onLogout={onLogout}
                        accountEmail={accountEmail}
                        onExport={onExport}
                        chatsCount={chatsCount}
                    />
                </div>
            ) : (
                <>
                    <div
                        data-testid="profile-panel-mobile-backdrop"
                        onClick={onClose}
                        className={`fixed inset-0 z-40 bg-stone-900/25 backdrop-blur-[2px] transition-opacity duration-200 ${
                            open ? "opacity-100" : "opacity-0 pointer-events-none"
                        }`}
                    />
                    <div
                        ref={mobileRef}
                        data-testid="profile-panel-mobile"
                        role="dialog"
                        aria-label="Profile and preferences"
                        className={`fixed left-0 right-0 bottom-0 z-50 bg-card rounded-t-3xl shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.25)] max-h-[92vh] flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)] ${
                            open ? "translate-y-0" : "translate-y-full"
                        }`}
                    >
                        <div className="flex justify-center pt-2.5 pb-1">
                            <span
                                aria-hidden
                                className="h-1 w-10 rounded-full bg-stone-300"
                            />
                        </div>
                        <PanelContent
                            onClose={onClose}
                            userProfile={userProfile}
                            onChangeUserProfile={onChangeUserProfile}
                            defaultStyle={defaultStyle}
                            onChangeDefaultStyle={onChangeDefaultStyle}
                            tonemirrorPrefs={tonemirrorPrefs}
                            onChangeTonemirrorPref={onChangeTonemirrorPref}
                            soundEnabled={soundEnabled}
                            onToggleSound={onToggleSound}
                            onResetAll={onResetAll}
                            onLogout={onLogout}
                            accountEmail={accountEmail}
                            onExport={onExport}
                            chatsCount={chatsCount}
                            compact
                        />
                    </div>
                </>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Content (shared between desktop overlay & mobile bottom sheet)
// ─────────────────────────────────────────────────────────────────────────────
const PanelContent = ({
    onClose,
    userProfile,
    onChangeUserProfile,
    defaultStyle,
    onChangeDefaultStyle,
    tonemirrorPrefs,
    onChangeTonemirrorPref,
    soundEnabled,
    onToggleSound,
    onResetAll,
    onLogout,
    accountEmail,
    onExport,
    chatsCount,
    compact = false,
}) => {
    // Accordion: only one section open at a time. Communication Style
    // expanded by default; users may collapse it or open a different one.
    const [expandedKey, setExpandedKey] = useState("communication-style");
    const [confirmReset, setConfirmReset] = useState(false);

    const toggle = (key) =>
        setExpandedKey((cur) => (cur === key ? null : key));

    return (
        <>
            <header className={`relative shrink-0 px-5 ${compact ? "pt-1" : "pt-4"} pb-4 border-b border-border/60`}>
                <button
                    type="button"
                    data-testid="btn-close-profile"
                    onClick={onClose}
                    aria-label="Close profile panel"
                    className="absolute right-4 top-4 inline-flex items-center justify-center h-8 w-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                >
                    <X className="h-4 w-4" />
                </button>
                <UserAccount
                    userProfile={userProfile}
                    onChangeUserProfile={onChangeUserProfile}
                />
            </header>

            <div className="flex-1 min-h-0 overflow-y-auto">
                <AccordionSection
                    index="1"
                    sectionKey="communication-style"
                    testid="section-communication-style"
                    title="Communication Style"
                    subtitle="How would you like ToneGuard to help you communicate most often?"
                    expanded={expandedKey === "communication-style"}
                    onToggle={toggle}
                    hideRowIcon
                >
                    <CommunicationStyleCards
                        value={defaultStyle}
                        onChange={onChangeDefaultStyle}
                    />
                </AccordionSection>

                <AccordionSection
                    index="2"
                    sectionKey="tonemirror"
                    testid="section-tonemirror"
                    icon={SlidersHorizontal}
                    title="ToneMirror Preferences"
                    subtitle="Customize how ToneMirror works for you"
                    expanded={expandedKey === "tonemirror"}
                    onToggle={toggle}
                >
                    <ToneMirrorContent
                        prefs={tonemirrorPrefs}
                        onChangePref={onChangeTonemirrorPref}
                        onClose={onClose}
                    />
                </AccordionSection>

                <AccordionSection
                    index="3"
                    sectionKey="appearance"
                    testid="section-appearance"
                    icon={Palette}
                    title="Appearance"
                    subtitle="Light · Dark · System"
                    expanded={expandedKey === "appearance"}
                    onToggle={toggle}
                >
                    <AppearanceContent
                        soundEnabled={soundEnabled}
                        onToggleSound={onToggleSound}
                    />
                </AccordionSection>

                <AccordionSection
                    index="4"
                    sectionKey="privacy"
                    testid="section-privacy"
                    icon={Lock}
                    title="Privacy & History"
                    subtitle="Manage your data and chat history"
                    expanded={expandedKey === "privacy"}
                    onToggle={toggle}
                >
                    <PrivacyContent chatsCount={chatsCount} onResetAll={onResetAll} />
                </AccordionSection>

                <AccordionSection
                    index="5"
                    sectionKey="export"
                    testid="section-export"
                    icon={Download}
                    title="Export Insights"
                    subtitle="Download your insights and patterns"
                    expanded={expandedKey === "export"}
                    onToggle={toggle}
                >
                    <ExportContent onExport={onExport} />
                </AccordionSection>

                <AccordionSection
                    index="6"
                    sectionKey="help"
                    testid="section-help"
                    icon={HelpCircle}
                    title="Help & Feedback"
                    subtitle="Help center, contact us, privacy policy"
                    expanded={expandedKey === "help"}
                    onToggle={toggle}
                    isLast
                >
                    <HelpContent />
                </AccordionSection>
            </div>

            <footer
                data-testid="section-footer"
                className="shrink-0 px-5 py-4 border-t border-border/60 flex items-center justify-between gap-3"
            >
                <div className="leading-tight min-w-0">
                    <p
                        data-testid="app-version"
                        className="text-[12px] font-medium text-muted-foreground"
                    >
                        ToneGuard Web{" "}
                        <span className="text-muted-foreground/70 font-normal">
                            {APP_VERSION}
                        </span>
                    </p>
                    {accountEmail && (
                        <p
                            data-testid="account-email"
                            className="text-[11px] text-muted-foreground/80 truncate"
                            title={accountEmail}
                        >
                            {accountEmail}
                        </p>
                    )}
                </div>
                {confirmReset ? (
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            data-testid="btn-confirm-logout"
                            onClick={() => {
                                setConfirmReset(false);
                                // Real auth logout — scoped storage keeps the
                                // user's chats intact for next login. Cloud
                                // sync also keeps a server-side copy. The
                                // destructive "Reset all data" flow lives in
                                // the Privacy & History section.
                                onLogout?.();
                            }}
                            className="text-[12px] font-semibold text-[#8C3A2F] hover:text-[#5e2a23] rounded-full px-3 py-1 hover:bg-[#F7EBE8]/60 transition-colors"
                        >
                            Confirm
                        </button>
                        <button
                            type="button"
                            onClick={() => setConfirmReset(false)}
                            className="text-[12px] text-muted-foreground hover:text-foreground rounded-full px-3 py-1 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        data-testid="btn-logout"
                        onClick={() => setConfirmReset(true)}
                        className="text-[13px] font-semibold text-[#C5482F] hover:text-[#8C3A2F] transition-colors"
                    >
                        Log out
                    </button>
                )}
            </footer>
        </>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// User account header — avatar (uploaded photo OR initial fallback) +
// editable name + tagline. Tapping the avatar opens the OS file picker;
// images are resized and stored as a small data URL on the user profile.
// ─────────────────────────────────────────────────────────────────────────────
const MAX_PHOTO_DIMENSION = 256; // px, square — keeps localStorage payload small
const MAX_PHOTO_BYTES = 1024 * 1024; // 1MB hard cap on the source file

async function fileToCompressedDataURL(file) {
    if (!file) return null;
    if (file.size > MAX_PHOTO_BYTES) {
        throw new Error("Image too large. Please pick one under 1MB.");
    }
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise((resolve, reject) => {
            const im = new Image();
            im.onload = () => resolve(im);
            im.onerror = () => reject(new Error("Couldn't read that image."));
            im.src = url;
        });
        // Square-crop to the shortest side, then scale down to MAX_PHOTO_DIMENSION.
        const minDim = Math.min(img.naturalWidth, img.naturalHeight);
        const sx = (img.naturalWidth - minDim) / 2;
        const sy = (img.naturalHeight - minDim) / 2;
        const canvas = document.createElement("canvas");
        canvas.width = MAX_PHOTO_DIMENSION;
        canvas.height = MAX_PHOTO_DIMENSION;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(
            img,
            sx,
            sy,
            minDim,
            minDim,
            0,
            0,
            MAX_PHOTO_DIMENSION,
            MAX_PHOTO_DIMENSION,
        );
        return canvas.toDataURL("image/jpeg", 0.85);
    } finally {
        URL.revokeObjectURL(url);
    }
}

const UserAccount = ({ userProfile, onChangeUserProfile }) => {
    const name = (userProfile?.name || "Friend").trim() || "Friend";
    const initial = name.charAt(0).toUpperCase();
    const photo = userProfile?.photo || null;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(name);
    const [uploadError, setUploadError] = useState("");
    const inputRef = useRef(null);
    const fileRef = useRef(null);

    useEffect(() => {
        if (editing) {
            setDraft(name);
            window.requestAnimationFrame(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            });
        }
    }, [editing, name]);

    const commit = () => {
        const next = (draft || "").trim() || "Friend";
        if (next !== name) onChangeUserProfile?.({ name: next });
        setEditing(false);
    };
    const cancel = () => {
        setDraft(name);
        setEditing(false);
    };

    const handlePickPhoto = () => {
        setUploadError("");
        fileRef.current?.click();
    };

    const handleFileSelected = async (e) => {
        const file = e.target.files?.[0];
        // Reset the input so picking the same file twice still fires onChange.
        if (fileRef.current) fileRef.current.value = "";
        if (!file) return;
        try {
            const dataUrl = await fileToCompressedDataURL(file);
            onChangeUserProfile?.({ photo: dataUrl });
        } catch (err) {
            setUploadError(err?.message || "Couldn't load that image.");
        }
    };

    const handleRemovePhoto = () => {
        setUploadError("");
        onChangeUserProfile?.({ photo: null });
    };

    return (
        <div className="flex items-center gap-3.5">
            <div className="relative shrink-0">
                <button
                    type="button"
                    data-testid="btn-upload-photo"
                    onClick={handlePickPhoto}
                    aria-label={photo ? "Change profile photo" : "Add profile photo"}
                    className="group/avatar relative h-14 w-14 rounded-full overflow-hidden bg-[#EAF1E8] text-[#3F523A] border border-[#D5E3D1] flex items-center justify-center text-[20px] font-semibold tracking-tight font-serif transition-shadow hover:shadow-[0_4px_14px_-6px_rgba(94,113,83,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5E7153]/40"
                >
                    {photo ? (
                        <img
                            data-testid="user-photo"
                            src={photo}
                            alt=""
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <span data-testid="user-initial" aria-hidden>
                            {initial}
                        </span>
                    )}
                    <span className="absolute inset-0 bg-stone-900/0 group-hover/avatar:bg-stone-900/35 transition-colors flex items-center justify-center">
                        <Camera className="h-4 w-4 text-white opacity-0 group-hover/avatar:opacity-100 transition-opacity" />
                    </span>
                </button>
                {photo && (
                    <button
                        type="button"
                        data-testid="btn-remove-photo"
                        onClick={handleRemovePhoto}
                        aria-label="Remove profile photo"
                        title="Remove photo"
                        className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-card border border-border text-muted-foreground hover:text-[#8C3A2F] hover:border-[#8C3A2F]/40 flex items-center justify-center shadow-sm transition-colors"
                    >
                        <Trash2 className="h-2.5 w-2.5" />
                    </button>
                )}
                <input
                    ref={fileRef}
                    data-testid="input-photo-file"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
                    onChange={handleFileSelected}
                    className="hidden"
                />
            </div>
            <div className="min-w-0 flex-1">
                {editing ? (
                    <input
                        ref={inputRef}
                        data-testid="input-user-name"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commit}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") commit();
                            if (e.key === "Escape") cancel();
                        }}
                        maxLength={32}
                        className="w-full font-serif text-[20px] leading-tight text-foreground bg-transparent border-b border-[#D5E3D1] outline-none focus:border-[#5E7153]"
                    />
                ) : (
                    <button
                        type="button"
                        data-testid="btn-edit-user-name"
                        onClick={() => setEditing(true)}
                        className="group/name flex items-center gap-1.5 max-w-full"
                    >
                        <span className="font-serif text-[20px] leading-tight text-foreground truncate">
                            {name}
                        </span>
                        <Pencil className="h-3 w-3 text-muted-foreground/60 opacity-0 group-hover/name:opacity-100 transition-opacity" />
                    </button>
                )}
                <p className="text-[12.5px] text-muted-foreground mt-0.5 leading-relaxed">
                    {uploadError ? (
                        <span className="text-[#8C3A2F]">{uploadError}</span>
                    ) : (
                        "Building better conversations."
                    )}
                </p>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Accordion section
// ─────────────────────────────────────────────────────────────────────────────
const AccordionSection = ({
    index,
    sectionKey,
    testid,
    icon: Icon,
    title,
    subtitle,
    expanded,
    onToggle,
    hideRowIcon = false,
    isLast = false,
    children,
}) => {
    return (
        <section
            data-testid={testid}
            data-expanded={expanded}
            className={`px-5 ${isLast ? "" : "border-b border-border/60"}`}
        >
            <button
                type="button"
                data-testid={`section-toggle-${sectionKey}`}
                onClick={() => onToggle(sectionKey)}
                aria-expanded={expanded}
                className="w-full py-4 flex items-center gap-3 text-left hover:bg-stone-50/50 -mx-5 px-5 transition-colors"
            >
                {!hideRowIcon && (
                    <span className="h-8 w-8 rounded-full bg-stone-100 border border-border/60 flex items-center justify-center shrink-0 text-stone-600">
                        {Icon && <Icon className="h-4 w-4" />}
                    </span>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                        <span className="text-[14px] font-semibold text-foreground">
                            {index}. {title}
                        </span>
                    </div>
                    {subtitle && (
                        <p className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">
                            {subtitle}
                        </p>
                    )}
                </div>
                <ChevronDown
                    className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                        expanded ? "rotate-180" : ""
                    }`}
                />
            </button>
            {expanded && (
                <div className="pb-5 -mt-1 tg-enter">{children}</div>
            )}
        </section>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Communication Style cards (vertical, full-width, with colored icon tile)
// ─────────────────────────────────────────────────────────────────────────────
const CommunicationStyleCards = ({ value, onChange }) => (
    <div role="radiogroup" data-testid="communication-style-cards">
        <div className="space-y-2">
            {STYLE_OPTIONS.map((opt) => {
                const selected = value === opt.key;
                const next = selected ? null : opt.key;
                const Icon = opt.icon;
                return (
                    <button
                        key={opt.key}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        data-testid={`style-card-${opt.key}`}
                        data-selected={selected}
                        onClick={() => onChange?.(next)}
                        className={[
                            "w-full flex items-center gap-3 px-3 py-3 rounded-2xl border text-left transition-all duration-200",
                            selected
                                ? "border-[#B7CDB1] bg-[#EAF1E8]/55 shadow-[0_2px_10px_-8px_rgba(94,113,83,0.45)]"
                                : "border-border/70 bg-card hover:border-stone-300 hover:bg-stone-50/70",
                        ].join(" ")}
                    >
                        <span
                            aria-hidden
                            className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${opt.iconClass}`}
                        >
                            <Icon className="h-4 w-4" />
                        </span>
                        <span className="flex-1 min-w-0 text-[14px] font-semibold text-foreground">
                            {opt.label}
                        </span>
                        <span
                            aria-hidden
                            className={`shrink-0 h-5 w-5 rounded-full flex items-center justify-center transition-colors ${
                                selected
                                    ? "bg-[#5E7153] text-white"
                                    : "border border-stone-300 bg-card"
                            }`}
                        >
                            {selected && <Check className="h-3 w-3" />}
                        </span>
                    </button>
                );
            })}
        </div>
        <p className="text-[12px] text-muted-foreground mt-3 leading-relaxed">
            This influences suggestions and insights — you&apos;re always free
            to explore any tone.
        </p>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ToneMirror toggles
// ─────────────────────────────────────────────────────────────────────────────
const ToneMirrorContent = ({ prefs = {}, onChangePref, onClose }) => (
    <div className="space-y-1 pt-1">
        {TONEMIRROR_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const checked = !!prefs[opt.key];
            return (
                <ToggleRow
                    key={opt.key}
                    testId={`pref-${opt.key}`}
                    icon={<Icon className="h-4 w-4" />}
                    title={opt.label}
                    subtitle={opt.hint}
                    checked={checked}
                    onChange={(next) => onChangePref?.(opt.key, next)}
                />
            );
        })}
        <Link
            to="/tonemirror"
            data-testid="link-open-tonemirror"
            onClick={onClose}
            className="mt-3 inline-flex items-center gap-2 text-[12.5px] font-medium text-[#4B6143] hover:text-[#2f3d29] transition-colors"
        >
            <BarChart3 className="h-3.5 w-3.5" />
            Open ToneMirror
        </Link>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Appearance
// ─────────────────────────────────────────────────────────────────────────────
const AppearanceContent = ({ soundEnabled, onToggleSound }) => (
    <div className="space-y-1 pt-1">
        <Row
            icon={<Sun className="h-4 w-4" />}
            title="Theme"
            subtitle="A calm light palette. Dark mode coming soon."
            trailing={
                <span className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                    Light
                </span>
            }
        />
        <ToggleRow
            testId="toggle-sound"
            icon={
                soundEnabled ? (
                    <Volume2 className="h-4 w-4" />
                ) : (
                    <VolumeX className="h-4 w-4" />
                )
            }
            title="Sound while reviewing"
            subtitle="Soft ambient pad while ToneGuard thinks."
            checked={soundEnabled}
            onChange={onToggleSound}
        />
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Privacy & History
// ─────────────────────────────────────────────────────────────────────────────
const PrivacyContent = ({ chatsCount, onResetAll }) => {
    const [confirming, setConfirming] = useState(false);
    return (
        <div className="pt-1 space-y-3">
            <div className="rounded-2xl border border-border/70 bg-stone-50/60 px-4 py-3.5 flex items-start gap-3">
                <span className="h-7 w-7 rounded-full bg-white border border-border/70 flex items-center justify-center shrink-0 text-muted-foreground">
                    <Lock className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0">
                    <p className="text-[13px] font-medium text-foreground">
                        Synced to your account.
                    </p>
                    <p className="text-[12.5px] text-muted-foreground mt-0.5 leading-relaxed">
                        All {chatsCount}{" "}
                        {chatsCount === 1 ? "chat is" : "chats are"} stored
                        privately under your account and follow you across
                        devices. Only the message you review is sent to
                        ToneGuard for analysis — never the full thread.
                    </p>
                </div>
            </div>

            <div className="rounded-2xl border border-[#E8C9C2] bg-[#FBF3F1]/70 px-4 py-3.5">
                <p className="text-[13px] font-medium text-foreground">
                    Reset all data
                </p>
                <p className="text-[12.5px] text-muted-foreground mt-0.5 leading-relaxed">
                    Permanently delete your chats, insights, and ToneMirror
                    history from this device and your account. This can&apos;t
                    be undone.
                </p>
                {confirming ? (
                    <div className="mt-3 flex items-center gap-2">
                        <button
                            type="button"
                            data-testid="btn-confirm-reset-device"
                            onClick={() => {
                                setConfirming(false);
                                onResetAll?.();
                            }}
                            className="text-[12px] font-semibold text-white bg-[#C5482F] hover:bg-[#8C3A2F] rounded-full px-3.5 py-1.5 transition-colors"
                        >
                            Yes, delete everything
                        </button>
                        <button
                            type="button"
                            data-testid="btn-cancel-reset-device"
                            onClick={() => setConfirming(false)}
                            className="text-[12px] text-muted-foreground hover:text-foreground rounded-full px-3 py-1.5 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        data-testid="btn-reset-device"
                        onClick={() => setConfirming(true)}
                        className="mt-3 inline-flex items-center gap-2 text-[13px] font-medium text-[#8C3A2F] hover:text-[#5e2a23] bg-white hover:bg-[#F7EBE8]/70 border border-[#E8C9C2] rounded-full px-3.5 py-1.5 transition-colors"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                        Reset all data
                    </button>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Export Insights
// ─────────────────────────────────────────────────────────────────────────────
const ExportContent = ({ onExport }) => (
    <div className="pt-1">
        <p className="text-[12.5px] text-muted-foreground leading-relaxed">
            Download a readable PDF of your chats, rewrites, and tone
            patterns — a quiet record of how you communicate.
        </p>
        <button
            type="button"
            data-testid="btn-export-insights"
            onClick={onExport}
            className="mt-3 inline-flex items-center gap-2 text-[13px] font-medium text-foreground hover:text-foreground bg-card hover:bg-stone-50 border border-border rounded-full px-3.5 py-1.5 transition-colors"
        >
            <Download className="h-3.5 w-3.5" />
            Download Chats &amp; Insights
        </button>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Help & Feedback
// ─────────────────────────────────────────────────────────────────────────────
const SUPPORT_EMAIL = "support@toneguard.in";
const HelpContent = () => (
    <div className="pt-1">
        <p className="text-[12.5px] text-muted-foreground leading-relaxed">
            ToneGuard is in early access — your notes shape the next versions.
        </p>
        <a
            href={`mailto:${SUPPORT_EMAIL}?subject=ToneGuard%20feedback`}
            data-testid="link-feedback"
            className="mt-3 inline-flex items-center gap-2 text-[13px] font-medium text-foreground hover:text-[#4B6143] transition-colors"
        >
            <LifeBuoy className="h-3.5 w-3.5" />
            Share a thought
        </a>
        <p className="mt-2 text-[11.5px] text-muted-foreground">
            Or email us directly at{" "}
            <a
                href={`mailto:${SUPPORT_EMAIL}?subject=ToneGuard%20feedback`}
                data-testid="text-support-email"
                className="font-medium text-foreground underline underline-offset-2 decoration-dotted hover:text-[#4B6143]"
            >
                {SUPPORT_EMAIL}
            </a>
            .
        </p>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Reusable rows
// ─────────────────────────────────────────────────────────────────────────────
const Row = ({ icon, title, subtitle, trailing }) => (
    <div className="flex items-center gap-3 px-2 py-2.5">
        <span className="h-7 w-7 rounded-full bg-stone-100/80 border border-border/60 flex items-center justify-center shrink-0 text-muted-foreground">
            {icon}
        </span>
        <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-foreground">{title}</p>
            {subtitle && (
                <p className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">
                    {subtitle}
                </p>
            )}
        </div>
        {trailing && <div className="shrink-0">{trailing}</div>}
    </div>
);

const ToggleRow = ({ icon, title, subtitle, checked, onChange, testId }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        data-testid={testId}
        onClick={() => onChange?.(!checked)}
        className="w-full flex items-center gap-3 px-2 py-2.5 rounded-xl text-left hover:bg-stone-50/70 transition-colors"
    >
        <span className="h-7 w-7 rounded-full bg-stone-100/80 border border-border/60 flex items-center justify-center shrink-0 text-muted-foreground">
            {icon}
        </span>
        <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-foreground">{title}</p>
            {subtitle && (
                <p className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">
                    {subtitle}
                </p>
            )}
        </div>
        <span
            aria-hidden
            className={`shrink-0 relative inline-flex h-5 w-9 rounded-full transition-colors ${
                checked ? "bg-[#5E7153]" : "bg-stone-300"
            }`}
        >
            <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                    checked ? "translate-x-4" : "translate-x-0.5"
                }`}
            />
        </span>
    </button>
);

export default ProfilePanel;

// Exported so callers (ToneGuardPage / AnalysisCard cascade) can resolve
// the user-facing Communication Style key to the internal style key the
// rewrite engine returns in `analysis.versions`.
export const STYLE_KEY_TO_INTERNAL = STYLE_OPTIONS.reduce((acc, opt) => {
    acc[opt.key] = opt.internal;
    return acc;
}, {});

export const STYLE_KEYS = STYLE_OPTIONS.map((o) => o.key);
